import { memo } from 'react';
import { cn } from '@/lib/utils';
import { OptimizedImage } from './OptimizedImage';

interface TeamLogoProps {
  logoUrl: string | null | undefined;
  teamName: string;
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  priority?: boolean;
}

// Mobile-optimized size map - smaller sizes for compact layouts
const sizeMap = {
  '2xs': { width: 18, height: 18, class: 'w-[18px] h-[18px]' },  // Ultra compact for mobile cards
  xs: { width: 22, height: 22, class: 'w-[22px] h-[22px]' },     // Compact for mobile
  sm: { width: 26, height: 26, class: 'w-[26px] h-[26px]' },     // Small
  md: { width: 32, height: 32, class: 'w-8 h-8' },               // Medium (default)
  lg: { width: 40, height: 40, class: 'w-10 h-10' },             // Large
  xl: { width: 56, height: 56, class: 'w-14 h-14' },             // Extra large
};

/**
 * Optimized team logo component with:
 * - Lazy loading
 * - Automatic fallback to initials
 * - Proper sizing to prevent CLS
 * - Memoization to prevent re-renders
 * - Mobile-optimized sizes
 */
function TeamLogoComponent({
  logoUrl,
  teamName,
  size = 'md',
  className,
  priority = false,
}: TeamLogoProps) {
  const { width, height, class: sizeClass } = sizeMap[size];
  const initials = teamName.substring(0, 2).toUpperCase();

  // Fallback initials - scaled font size based on logo size
  const fontSize = size === '2xs' || size === 'xs' ? 'text-[8px]' : size === 'sm' ? 'text-[9px]' : 'text-xs';

  if (!logoUrl) {
    return (
      <div 
        className={cn(
          sizeClass,
          'rounded-full bg-secondary flex items-center justify-center font-bold shrink-0',
          fontSize,
          className
        )}
        aria-hidden="true"
      >
        {initials}
      </div>
    );
  }

  return (
    <OptimizedImage
      src={logoUrl}
      alt=""
      width={width}
      height={height}
      priority={priority}
      fallbackName={teamName}
      className={cn(sizeClass, 'object-contain shrink-0', className)}
    />
  );
}

export const TeamLogo = memo(TeamLogoComponent, (prev, next) => {
  return (
    prev.logoUrl === next.logoUrl &&
    prev.teamName === next.teamName &&
    prev.size === next.size &&
    prev.priority === next.priority
  );
});
